import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import {
  type ReactElement,
  type ReactNode,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export const HCA_PHONE_DISPLAY = "+420 603 905 212";
export const HCA_PHONE_TEL = "+420603905212";

interface OpenKontaktOptions {
  /** Předvyplněná zpráva — typicky kontext z místa odkud uživatel přišel. */
  initialMessage?: string;
}

interface PublicActionsContextValue {
  openDiagnoza: () => void;
  openKontakt: (options?: OpenKontaktOptions) => void;
}

const PublicActionsContext = createContext<PublicActionsContextValue | null>(null);

function shouldOpenDiagnozaFromUrl() {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.has("diagnostika") || params.has("diagnoza");
}

function removeDiagnozaParams() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const hadDiagnoza = url.searchParams.has("diagnostika") || url.searchParams.has("diagnoza");
  url.searchParams.delete("diagnostika");
  url.searchParams.delete("diagnoza");
  if (!hadDiagnoza) return;
  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, "", nextUrl);
}

export function PublicActionsProvider({
  children,
  diagnozaContent,
  kontaktContent,
}: {
  children: ReactNode;
  diagnozaContent?: ReactNode;
  kontaktContent?: ReactNode;
}) {
  const [diagnozaOpen, setDiagnozaOpen] = useState(false);
  const [kontaktOpen, setKontaktOpen] = useState(false);
  const [kontaktInitialMessage, setKontaktInitialMessage] = useState<string | undefined>(undefined);
  const [cookieVisible, setCookieVisible] = useState(false);

  const openDiagnoza = useCallback(() => {
    setDiagnozaOpen(true);
  }, []);
  const openKontakt = useCallback((options?: OpenKontaktOptions) => {
    setKontaktInitialMessage(options?.initialMessage);
    setKontaktOpen(true);
  }, []);

  useEffect(() => {
    if (shouldOpenDiagnozaFromUrl()) setDiagnozaOpen(true);
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    setCookieVisible(window.localStorage.getItem("hca-cookie-consent") !== "accepted");
  }, []);

  const value = useMemo(() => ({ openDiagnoza, openKontakt }), [openDiagnoza, openKontakt]);

  const handleDiagnozaOpenChange = (open: boolean) => {
    setDiagnozaOpen(open);
    if (!open) removeDiagnozaParams();
  };

  // Inject onNavigate so the wizard's result CTA closes the modal before it
  // navigates to /seminare/$slug or /terminy (otherwise the dialog would stay
  // open on top of the new route).
  const diagnozaChild =
    diagnozaContent && isValidElement(diagnozaContent)
      ? cloneElement(diagnozaContent as ReactElement<{ onNavigate?: () => void }>, {
          onNavigate: () => handleDiagnozaOpenChange(false),
        })
      : diagnozaContent;

  const closeKontakt = useCallback(() => setKontaktOpen(false), []);
  const kontaktChild =
    kontaktContent && isValidElement(kontaktContent)
      ? cloneElement(
          kontaktContent as ReactElement<{
            onSubmitted?: () => void;
            initialMessage?: string;
          }>,
          {
            onSubmitted: closeKontakt,
            ...(kontaktInitialMessage ? { initialMessage: kontaktInitialMessage } : {}),
          },
        )
      : kontaktContent;

  return (
    <PublicActionsContext.Provider value={value}>
      {children}
      {diagnozaContent ? (
        <Dialog open={diagnozaOpen} onOpenChange={handleDiagnozaOpenChange}>
          <DialogContent
            className="flex h-[min(92vh,720px)] max-w-3xl flex-col gap-0 overflow-hidden p-0"
            data-testid="hca-diagnoza-modal"
          >
            <DialogTitle className="sr-only">Manažerská diagnostika firmy</DialogTitle>
            <DialogDescription className="sr-only">
              Krátký formulář, který doporučí vhodný HCA program podle stavu firmy.
            </DialogDescription>
            {diagnozaChild}
          </DialogContent>
        </Dialog>
      ) : null}
      {kontaktContent ? (
        <Dialog open={kontaktOpen} onOpenChange={setKontaktOpen}>
          <DialogContent className="max-w-2xl" data-testid="hca-kontakt-modal">
            <DialogTitle>Kontaktujte nás</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Napište nám, ozveme se zpravidla do následujícího pracovního dne.
            </DialogDescription>
            {/* `key` ensures a fresh form (with the latest prefill) whenever
                the dialog re-opens with a different initialMessage. */}
            <div key={kontaktInitialMessage ?? "blank"} className="mt-2">
              {kontaktChild}
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
      {cookieVisible ? <CookieConsent onAccept={() => setCookieVisible(false)} /> : null}
    </PublicActionsContext.Provider>
  );
}

export function usePublicActions() {
  const context = useContext(PublicActionsContext);
  if (!context) {
    throw new Error("usePublicActions must be used within PublicActionsProvider");
  }
  return context;
}

function CookieConsent({ onAccept }: { onAccept: () => void }) {
  const accept = () => {
    window.localStorage.setItem("hca-cookie-consent", "accepted");
    onAccept();
  };

  return (
    <div
      className="fixed bottom-24 left-4 right-4 z-50 rounded-lg border border-border bg-background p-4 shadow-xl md:bottom-6 md:right-auto md:max-w-md"
      data-testid="hca-cookie-consent"
    >
      <div className="text-sm font-semibold">Cookies a soukromí</div>
      <p className="mt-1 text-sm leading-6 text-muted-foreground">
        Web používá nezbytné cookies pro správné fungování. Analytické nebo marketingové měření bude
        zapnuté až podle finálního nastavení.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" onClick={accept} data-testid="hca-cookie-consent-accept">
          Rozumím
        </Button>
        <a
          href="/gdpr"
          className="text-sm font-medium text-primary hover:underline"
          data-testid="hca-cookie-consent-gdpr"
        >
          GDPR
        </a>
      </div>
    </div>
  );
}
